import { c as _c } from "react/compiler-runtime";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { api, type AuditEntry } from "@/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { auditActionLabel, auditEntityLabel, auditEntityLink } from "@/lib/audit-labels";
import { formatDateTime } from "@/lib/utils";

/**
 * Раздел «Журнал» — действия в консоли площадки.
 *
 * Отвечает на вопрос, ради которого таблицу и заводили: кто снял товар с
 * публикации, кто заблокировал магазин, кто поменял комиссию и когда. Записи
 * делает перехватчик на шлюзе, поэтому в журнал попадает ВСЁ, что меняет
 * состояние через консоль, включая отказы доступа — попытка зайти в чужой
 * раздел видна так же, как удавшееся действие.
 *
 * Правка записей не предусмотрена намеренно: журнал, который можно
 * отредактировать, не журнал.
 */

const PAGE_SIZE = 50;

/** Успех, отказ доступа и всё прочее — три разных цвета, чтобы читать глазами. */
const statusVariant = (status?: number): "default" | "destructive" | "secondary" => status == null ? "secondary" : status < 300 ? "default" : status === 403 || status === 401 ? "destructive" : "secondary";
const AuditRow = t0 => {
  const $ = _c(50);
  const {
    entry,
    onPick
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== entry.createdAt) {
    t1 = formatDateTime(entry.createdAt);
    $[0] = entry.createdAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <td className="whitespace-nowrap p-2 text-muted-foreground">{t1}</td>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== entry.actorName || $[5] !== t) {
    t3 = entry.actorName || <span className="text-muted-foreground">{t("audit.noActor")}</span>;
    $[4] = entry.actorName;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== entry.actorEmail) {
    t4 = entry.actorEmail && <div className="text-xs text-muted-foreground">{entry.actorEmail}</div>;
    $[7] = entry.actorEmail;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <td className="p-2">{t3}{t4}</td>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = entry.entityKind;
  let t7;
  if ($[12] !== entry.entityKind) {
    t7 = auditEntityLabel(entry.entityKind);
    $[12] = entry.entityKind;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== entry.entityKind || $[15] !== t7) {
    t8 = <td className="p-2"><Badge variant="outline" title={t6}>{t7}</Badge></td>;
    $[14] = entry.entityKind;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = entry.action;
  let t10;
  if ($[17] !== entry.action) {
    t10 = auditActionLabel(entry.action);
    $[17] = entry.action;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== entry.action || $[20] !== t10) {
    t11 = <td className="p-2 font-medium" title={t9}>{t10}</td>;
    $[19] = entry.action;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== entry.entityId || $[23] !== entry.entityKind) {
    t12 = auditEntityLink(entry.entityKind, entry.entityId) ? <Link to={auditEntityLink(entry.entityKind, entry.entityId) as string} className="underline-offset-4 hover:underline">{entry.entityId}</Link> : entry.entityId;
    $[22] = entry.entityId;
    $[23] = entry.entityKind;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== entry.entityId || $[26] !== t12) {
    t13 = <td className="max-w-[220px] truncate p-2 text-muted-foreground" title={entry.entityId}>{t12}</td>;
    $[25] = entry.entityId;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== entry.statusCode) {
    t14 = statusVariant(entry.statusCode);
    $[28] = entry.statusCode;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== entry.method || $[31] !== entry.statusCode || $[32] !== t14) {
    t15 = <td className="p-2"><Badge variant={t14}>{entry.method} {entry.statusCode}</Badge></td>;
    $[30] = entry.method;
    $[31] = entry.statusCode;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== entry.reason) {
    t16 = <td className="max-w-[260px] truncate p-2 text-muted-foreground" title={entry.reason}>{entry.reason}</td>;
    $[34] = entry.reason;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t) {
    t17 = t("audit.details");
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== onPick || $[39] !== t17) {
    t18 = <td className="p-2 text-right"><Button variant="ghost" size="sm" onClick={onPick}>{t17}</Button></td>;
    $[38] = onPick;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t11 || $[42] !== t13 || $[43] !== t15 || $[44] !== t16 || $[45] !== t18 || $[46] !== t2 || $[47] !== t5 || $[48] !== t8) {
    t19 = <tr className="border-b last:border-0 hover:bg-muted/40">{t2}{t5}{t8}{t11}{t13}{t15}{t16}{t18}</tr>;
    $[41] = t11;
    $[42] = t13;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t2;
    $[47] = t5;
    $[48] = t8;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
};
export function AuditPage() {
  const $ = _c(214);
  const {
    t
  } = useTranslation();
  const [searchParams] = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("q") ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [q, setQ] = useState(t0);
  const [entityKind, setEntityKind] = useState("");
  const [action, setAction] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState(null);
  const t1 = dateFrom ? `${dateFrom}T00:00:00+03:00` : undefined;
  const t2 = dateTo ? `${dateTo}T23:59:59.999+03:00` : undefined;
  let t3;
  if ($[2] !== action || $[3] !== actorUserId || $[4] !== entityKind || $[5] !== page || $[6] !== q || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      q,
      entityKind,
      action,
      actorUserId,
      from: t1,
      to: t2,
      page,
      pageSize: PAGE_SIZE
    };
    $[2] = action;
    $[3] = actorUserId;
    $[4] = entityKind;
    $[5] = page;
    $[6] = q;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const filter = t3;
  let t4;
  if ($[10] !== filter) {
    t4 = {
      queryKey: ["audit", filter],
      queryFn: () => api.audit.list(filter)
    };
    $[10] = filter;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    data,
    isLoading,
    error
  } = useQuery(t4);
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      queryKey: ["audit", "facets"],
      queryFn: _temp
    };
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data: facets
  } = useQuery(t5);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setQ("");
      setEntityKind("");
      setAction("");
      setActorUserId("");
      setDateFrom("");
      setDateTo("");
      setPage(1);
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const reset = t6;
  let t7;
  if ($[14] !== t) {
    t7 = t("audit.title");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <h1 className="text-2xl font-semibold">{t7}</h1>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("audit.subtitle");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <p className="text-sm text-muted-foreground">{t9}</p>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div data-tour="audit-intro">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("audit.search");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12) {
    t13 = <Label htmlFor="audit-q">{t12}</Label>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("audit.searchPlaceholder");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => {
      setQ(e.target.value);
      setPage(1);
    };
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== q || $[33] !== t14) {
    t16 = <Input id="audit-q" value={q} placeholder={t14} onChange={t15} />;
    $[32] = q;
    $[33] = t14;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t16) {
    t17 = <div className="flex flex-col gap-1">{t13}{t16}</div>;
    $[35] = t13;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t) {
    t18 = t("audit.section");
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <Label htmlFor="audit-kind">{t18}</Label>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = e_0 => {
      setEntityKind(e_0.target.value);
      setPage(1);
    };
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t) {
    t21 = t("audit.any");
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t21) {
    t22 = <option value="">{t21}</option>;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== facets?.kinds) {
    t23 = facets?.kinds.map(_temp2);
    $[47] = facets?.kinds;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== entityKind || $[50] !== t22 || $[51] !== t23) {
    t24 = <select id="audit-kind" className="h-9 w-full min-w-0 rounded-md border bg-background px-2 text-sm" value={entityKind} onChange={t20}>{t22}{t23}</select>;
    $[49] = entityKind;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t19 || $[54] !== t24) {
    t25 = <div className="flex flex-col gap-1">{t19}{t24}</div>;
    $[53] = t19;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("audit.action");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <Label htmlFor="audit-action">{t26}</Label>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = e_1 => {
      setAction(e_1.target.value);
      setPage(1);
    };
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t) {
    t29 = t("audit.any");
    $[61] = t;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t29) {
    t30 = <option value="">{t29}</option>;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== facets?.actions) {
    t31 = facets?.actions.map(_temp3);
    $[65] = facets?.actions;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== action || $[68] !== t30 || $[69] !== t31) {
    t32 = <select id="audit-action" className="h-9 w-full min-w-0 rounded-md border bg-background px-2 text-sm" value={action} onChange={t28}>{t30}{t31}</select>;
    $[67] = action;
    $[68] = t30;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t27 || $[72] !== t32) {
    t33 = <div className="flex flex-col gap-1">{t27}{t32}</div>;
    $[71] = t27;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t) {
    t34 = t("audit.actor");
    $[74] = t;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t34) {
    t35 = <Label htmlFor="audit-actor">{t34}</Label>;
    $[76] = t34;
    $[77] = t35;
  } else {
    t35 = $[77];
  }
  let t36;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_2 => {
      setActorUserId(e_2.target.value);
      setPage(1);
    };
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== t) {
    t37 = t("audit.any");
    $[79] = t;
    $[80] = t37;
  } else {
    t37 = $[80];
  }
  let t38;
  if ($[81] !== t37) {
    t38 = <option value="">{t37}</option>;
    $[81] = t37;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== facets?.actors) {
    t39 = facets?.actors.map(_temp4);
    $[83] = facets?.actors;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] !== actorUserId || $[86] !== t38 || $[87] !== t39) {
    t40 = <select id="audit-actor" className="h-9 w-full min-w-0 rounded-md border bg-background px-2 text-sm" value={actorUserId} onChange={t36}>{t38}{t39}</select>;
    $[85] = actorUserId;
    $[86] = t38;
    $[87] = t39;
    $[88] = t40;
  } else {
    t40 = $[88];
  }
  let t41;
  if ($[89] !== t35 || $[90] !== t40) {
    t41 = <div className="flex flex-col gap-1">{t35}{t40}</div>;
    $[89] = t35;
    $[90] = t40;
    $[91] = t41;
  } else {
    t41 = $[91];
  }
  let t42;
  if ($[92] !== t) {
    t42 = t("audit.from");
    $[92] = t;
    $[93] = t42;
  } else {
    t42 = $[93];
  }
  let t43;
  if ($[94] !== t42) {
    t43 = <Label htmlFor="audit-from">{t42}</Label>;
    $[94] = t42;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  const t44 = dateTo || undefined;
  let t45;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = e_3 => {
      setDateFrom(e_3.target.value);
      setPage(1);
    };
    $[96] = t45;
  } else {
    t45 = $[96];
  }
  let t46;
  if ($[97] !== dateFrom || $[98] !== t44) {
    t46 = <Input id="audit-from" type="date" value={dateFrom} max={t44} onChange={t45} />;
    $[97] = dateFrom;
    $[98] = t44;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== t43 || $[101] !== t46) {
    t47 = <div className="flex flex-col gap-1">{t43}{t46}</div>;
    $[100] = t43;
    $[101] = t46;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t) {
    t48 = t("audit.to");
    $[103] = t;
    $[104] = t48;
  } else {
    t48 = $[104];
  }
  let t49;
  if ($[105] !== t48) {
    t49 = <Label htmlFor="audit-to">{t48}</Label>;
    $[105] = t48;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  const t50 = dateFrom || undefined;
  let t51;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = e_4 => {
      setDateTo(e_4.target.value);
      setPage(1);
    };
    $[107] = t51;
  } else {
    t51 = $[107];
  }
  let t52;
  if ($[108] !== dateTo || $[109] !== t50) {
    t52 = <Input id="audit-to" type="date" value={dateTo} min={t50} onChange={t51} />;
    $[108] = dateTo;
    $[109] = t50;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] !== t49 || $[112] !== t52) {
    t53 = <div className="flex flex-col gap-1">{t49}{t52}</div>;
    $[111] = t49;
    $[112] = t52;
    $[113] = t53;
  } else {
    t53 = $[113];
  }
  let t54;
  if ($[114] !== t17 || $[115] !== t25 || $[116] !== t33 || $[117] !== t41 || $[118] !== t47 || $[119] !== t53) {
    t54 = <div className="grid grid-cols-1 gap-3 rounded-lg border p-4 md:grid-cols-3 xl:grid-cols-6" data-tour="audit-filters">{t17}{t25}{t33}{t41}{t47}{t53}</div>;
    $[114] = t17;
    $[115] = t25;
    $[116] = t33;
    $[117] = t41;
    $[118] = t47;
    $[119] = t53;
    $[120] = t54;
  } else {
    t54 = $[120];
  }
  let t55;
  if ($[121] !== error) {
    t55 = error && <p className="text-sm text-destructive">{String((error as Error).message)}</p>;
    $[121] = error;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  let t56;
  if ($[123] !== t) {
    t56 = t("audit.when");
    $[123] = t;
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== t56) {
    t57 = <th className="p-2 font-medium">{t56}</th>;
    $[125] = t56;
    $[126] = t57;
  } else {
    t57 = $[126];
  }
  let t58;
  if ($[127] !== t) {
    t58 = t("audit.actor");
    $[127] = t;
    $[128] = t58;
  } else {
    t58 = $[128];
  }
  let t59;
  if ($[129] !== t58) {
    t59 = <th className="p-2 font-medium">{t58}</th>;
    $[129] = t58;
    $[130] = t59;
  } else {
    t59 = $[130];
  }
  let t60;
  if ($[131] !== t) {
    t60 = t("audit.section");
    $[131] = t;
    $[132] = t60;
  } else {
    t60 = $[132];
  }
  let t61;
  if ($[133] !== t60) {
    t61 = <th className="p-2 font-medium">{t60}</th>;
    $[133] = t60;
    $[134] = t61;
  } else {
    t61 = $[134];
  }
  let t62;
  if ($[135] !== t) {
    t62 = t("audit.action");
    $[135] = t;
    $[136] = t62;
  } else {
    t62 = $[136];
  }
  let t63;
  if ($[137] !== t62) {
    t63 = <th className="p-2 font-medium">{t62}</th>;
    $[137] = t62;
    $[138] = t63;
  } else {
    t63 = $[138];
  }
  let t64;
  if ($[139] !== t) {
    t64 = t("audit.object");
    $[139] = t;
    $[140] = t64;
  } else {
    t64 = $[140];
  }
  let t65;
  if ($[141] !== t64) {
    t65 = <th className="p-2 font-medium">{t64}</th>;
    $[141] = t64;
    $[142] = t65;
  } else {
    t65 = $[142];
  }
  let t66;
  if ($[143] !== t) {
    t66 = t("audit.result");
    $[143] = t;
    $[144] = t66;
  } else {
    t66 = $[144];
  }
  let t67;
  if ($[145] !== t66) {
    t67 = <th className="p-2 font-medium">{t66}</th>;
    $[145] = t66;
    $[146] = t67;
  } else {
    t67 = $[146];
  }
  let t68;
  if ($[147] !== t) {
    t68 = t("audit.reason");
    $[147] = t;
    $[148] = t68;
  } else {
    t68 = $[148];
  }
  let t69;
  if ($[149] !== t68) {
    t69 = <th className="p-2 font-medium">{t68}</th>;
    $[149] = t68;
    $[150] = t69;
  } else {
    t69 = $[150];
  }
  let t70;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <th />;
    $[151] = t70;
  } else {
    t70 = $[151];
  }
  let t71;
  if ($[152] !== t57 || $[153] !== t59 || $[154] !== t61 || $[155] !== t63 || $[156] !== t65 || $[157] !== t67 || $[158] !== t69) {
    t71 = <thead className="bg-muted/50 text-left"><tr>{t57}{t59}{t61}{t63}{t65}{t67}{t69}{t70}</tr></thead>;
    $[152] = t57;
    $[153] = t59;
    $[154] = t61;
    $[155] = t63;
    $[156] = t65;
    $[157] = t67;
    $[158] = t69;
    $[159] = t71;
  } else {
    t71 = $[159];
  }
  let t72;
  if ($[160] !== data?.items) {
    t72 = data?.items.map(entry => <AuditRow key={entry.id} entry={entry} onPick={() => setPicked(entry)} />);
    $[160] = data?.items;
    $[161] = t72;
  } else {
    t72 = $[161];
  }
  let t73;
  if ($[162] !== data?.items || $[163] !== isLoading || $[164] !== t) {
    t73 = !isLoading && data?.items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">{t("audit.empty")}</td></tr>;
    $[162] = data?.items;
    $[163] = isLoading;
    $[164] = t;
    $[165] = t73;
  } else {
    t73 = $[165];
  }
  let t74;
  if ($[166] !== t72 || $[167] !== t73) {
    t74 = <tbody>{t72}{t73}</tbody>;
    $[166] = t72;
    $[167] = t73;
    $[168] = t74;
  } else {
    t74 = $[168];
  }
  let t75;
  if ($[169] !== t71 || $[170] !== t74) {
    t75 = <div className="overflow-x-auto rounded-lg border" data-tour="audit-table"><table className="w-full text-sm">{t71}{t74}</table></div>;
    $[169] = t71;
    $[170] = t74;
    $[171] = t75;
  } else {
    t75 = $[171];
  }
  const t76 = data?.total ?? 0;
  let t77;
  if ($[172] !== t || $[173] !== t76) {
    t77 = t("audit.total", {
      count: t76
    });
    $[172] = t;
    $[173] = t76;
    $[174] = t77;
  } else {
    t77 = $[174];
  }
  let t78;
  if ($[175] !== t77) {
    t78 = <span>{t77}</span>;
    $[175] = t77;
    $[176] = t78;
  } else {
    t78 = $[176];
  }
  let t79;
  if ($[177] !== t) {
    t79 = t("audit.reset");
    $[177] = t;
    $[178] = t79;
  } else {
    t79 = $[178];
  }
  let t80;
  if ($[179] !== t79) {
    t80 = <Button variant="outline" size="sm" onClick={reset}>{t79}</Button>;
    $[179] = t79;
    $[180] = t80;
  } else {
    t80 = $[180];
  }
  const t81 = page <= 1;
  let t82;
  if ($[181] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = () => setPage(_temp5);
    $[181] = t82;
  } else {
    t82 = $[181];
  }
  let t83;
  if ($[182] !== t81) {
    t83 = <Button variant="outline" size="sm" disabled={t81} onClick={t82}>←</Button>;
    $[182] = t81;
    $[183] = t83;
  } else {
    t83 = $[183];
  }
  let t84;
  if ($[184] !== page || $[185] !== totalPages) {
    t84 = <span>{page} / {totalPages}</span>;
    $[184] = page;
    $[185] = totalPages;
    $[186] = t84;
  } else {
    t84 = $[186];
  }
  const t85 = page >= totalPages;
  let t86;
  if ($[187] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = () => setPage(_temp6);
    $[187] = t86;
  } else {
    t86 = $[187];
  }
  let t87;
  if ($[188] !== t85) {
    t87 = <Button variant="outline" size="sm" disabled={t85} onClick={t86}>→</Button>;
    $[188] = t85;
    $[189] = t87;
  } else {
    t87 = $[189];
  }
  let t88;
  if ($[190] !== t80 || $[191] !== t83 || $[192] !== t84 || $[193] !== t87) {
    t88 = <div className="flex items-center gap-2">{t80}{t83}{t84}{t87}</div>;
    $[190] = t80;
    $[191] = t83;
    $[192] = t84;
    $[193] = t87;
    $[194] = t88;
  } else {
    t88 = $[194];
  }
  let t89;
  if ($[195] !== t78 || $[196] !== t88) {
    t89 = <div className="flex items-center justify-between text-sm text-muted-foreground">{t78}{t88}</div>;
    $[195] = t78;
    $[196] = t88;
    $[197] = t89;
  } else {
    t89 = $[197];
  }
  const t90 = Boolean(picked);
  let t91;
  if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = open => !open && setPicked(null);
    $[198] = t91;
  } else {
    t91 = $[198];
  }
  let t92;
  if ($[199] !== picked || $[200] !== t) {
    t92 = picked && <><SheetHeader><SheetTitle>{auditEntityLabel(picked.entityKind)} · {auditActionLabel(picked.action)}</SheetTitle><SheetDescription>{picked.method} {picked.path}</SheetDescription></SheetHeader><div className="flex flex-col gap-3 px-4 pb-6"><p className="text-xs text-muted-foreground">{picked.route}</p><dl className="grid gap-2 text-sm sm:grid-cols-2"><div><dt className="text-muted-foreground">{t("audit.when")}</dt><dd>{formatDateTime(picked.createdAt)}</dd></div><div><dt className="text-muted-foreground">{t("audit.actor")}</dt><dd>{picked.actorName || t("audit.noActor")}</dd></div><div><dt className="text-muted-foreground">{t("audit.result")}</dt><dd>{picked.method} {picked.statusCode}</dd></div><div><dt className="text-muted-foreground">{t("audit.duration")}</dt><dd>{picked.durationMs == null ? "\u2014" : `${picked.durationMs} мс`}</dd></div><div><dt className="text-muted-foreground">IP</dt><dd>{picked.ip ?? "\u2014"}</dd></div><div className="min-w-0"><dt className="text-muted-foreground">{t("audit.client")}</dt><dd className="truncate" title={picked.userAgent}>{picked.userAgent ?? "\u2014"}</dd></div></dl>{picked.reason && <p className="text-sm"><span className="text-muted-foreground">{t("audit.reason")}: </span>{picked.reason}</p>}<pre className="max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">{JSON.stringify(picked.metadata, null, 2)}</pre></div></>;
    $[199] = picked;
    $[200] = t;
    $[201] = t92;
  } else {
    t92 = $[201];
  }
  let t93;
  if ($[202] !== t92) {
    t93 = <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">{t92}</SheetContent>;
    $[202] = t92;
    $[203] = t93;
  } else {
    t93 = $[203];
  }
  let t94;
  if ($[204] !== t90 || $[205] !== t93) {
    t94 = <Sheet open={t90} onOpenChange={t91}>{t93}</Sheet>;
    $[204] = t90;
    $[205] = t93;
    $[206] = t94;
  } else {
    t94 = $[206];
  }
  let t95;
  if ($[207] !== t11 || $[208] !== t54 || $[209] !== t55 || $[210] !== t75 || $[211] !== t89 || $[212] !== t94) {
    t95 = <div className="flex flex-col gap-4">{t11}{t54}{t55}{t75}{t89}{t94}</div>;
    $[207] = t11;
    $[208] = t54;
    $[209] = t55;
    $[210] = t75;
    $[211] = t89;
    $[212] = t94;
    $[213] = t95;
  } else {
    t95 = $[213];
  }
  return t95;
}
function _temp6(p_0) {
  return p_0 + 1;
}
function _temp5(p) {
  return p - 1;
}
function _temp4(a_0) {
  return <option key={a_0.id} value={a_0.id}>{a_0.name || a_0.id} ({a_0.count})</option>;
}
function _temp3(a) {
  return <option key={a.value} value={a.value}>{auditActionLabel(a.value)} ({a.count})</option>;
}
function _temp2(k) {
  return <option key={k.value} value={k.value}>{auditEntityLabel(k.value)} ({k.count})</option>;
}
function _temp() {
  return api.audit.facets();
}